import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Nike Jordan-inspired section banner.
 * Clean white/light background, massive bold uppercase title,
 * subtle subtitle, optional CTA pill button.
 *
 * Props:
 *   title      – Bold uppercase headline
 *   subtitle   – Gray supporting text
 *   ctaText    – Pill button label
 *   ctaLink    – Button destination
 *   accent     – "black" (default) | "red" — CTA button color
 *   compact    – Smaller vertical spacing
 */
const SectionDivider = ({
  title,
  subtitle,
  ctaText,
  ctaLink,
  accent = 'black',
  compact = false,
  className,
}) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const ease = [0.22, 1, 0.36, 1]; // smooth cubic

  // No content → thin line spacer
  if (!title && !subtitle && !ctaText) {
    return (
      <div ref={ref} className={cn('w-full py-3 md:py-4', className)}>
        <motion.div
          className="mx-auto w-16 h-px bg-gray-200"
          initial={{ opacity: 0, scaleX: 0 }}
          animate={isInView ? { opacity: 1, scaleX: 1 } : {}}
          transition={{ duration: 0.4, ease }}
        />
      </div>
    );
  }

  const ctaColors = {
    black: 'bg-black text-white hover:bg-gray-900',
    red: 'bg-red-600 text-white hover:bg-red-700',
  };

  return (
    <section
      ref={ref}
      className={cn(
        'w-full bg-white',
        compact ? 'py-6 md:py-8' : 'py-8 md:py-10',
        className
      )}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Title — massive bold uppercase */}
        <motion.h2
          className={cn(
            'font-black uppercase tracking-tight leading-[0.95] text-black',
            compact
              ? 'text-3xl sm:text-4xl md:text-5xl'
              : 'text-4xl sm:text-5xl md:text-6xl lg:text-7xl'
          )}
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease }}
        >
          {title}
        </motion.h2>

        {/* Subtitle */}
        {subtitle && (
          <motion.p
            className={cn(
              'mt-2 md:mt-3 text-gray-500 font-normal',
              compact ? 'text-sm sm:text-base' : 'text-base sm:text-lg md:text-xl'
            )}
            initial={{ opacity: 0, y: 24 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.12, ease }}
          >
            {subtitle}
          </motion.p>
        )}

        {/* CTA pill button */}
        {ctaText && ctaLink && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.22, ease }}
            className="mt-4 md:mt-5"
          >
            <Link
              to={ctaLink}
              className={cn(
                'inline-flex items-center gap-2 px-7 py-3 text-sm font-bold uppercase tracking-wider rounded-full transition-colors duration-200',
                ctaColors[accent] || ctaColors.black
              )}
            >
              {ctaText}
              <ArrowRight size={16} strokeWidth={2.5} />
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default SectionDivider;
